// src/pages/HowItWorksPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, CreditCard, MapPin, DollarSign, ArrowRight, ArrowDown, CheckCircle, Pill, Shield, Clock, Heart, Users, Smartphone } from 'lucide-react';

const HowItWorksPage = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const colors = {
    primary: '#2055df',
    primaryDark: '#1a44b8',
    secondary: '#0f172a',
    accent: '#06b6d4',
    success: '#10b981',
    warning: '#f59e0b',
    bg: '#f8fafc',
    white: '#ffffff',
    gray100: '#f1f5f9',
    gray200: '#e2e8f0',
    gray400: '#94a3b8',
    gray500: '#64748b',
    gray600: '#475569',
    gray800: '#1e293b',
    gray900: '#0f172a'
  };

  const steps = [
    {
      number: '1',
      icon: <Search size={28} />,
      title: 'Search Your Medication',
      description: 'Type the name of your prescription drug into our search tool. We\'ll instantly show you discounted prices at pharmacies near you.',
      tip: 'Try searching the generic name for even bigger savings',
      color: colors.primary
    },
    {
      number: '2',
      icon: <DollarSign size={28} />,
      title: 'Compare Prices',
      description: 'See side-by-side pricing from pharmacies in your area. Prices can vary by hundreds of dollars between pharmacies — we help you find the lowest one.',
      tip: 'Costco and Walmart often have the lowest prices',
      color: colors.success
    },
    {
      number: '3',
      icon: <CreditCard size={28} />,
      title: 'Get Your Free Card',
      description: 'Sign up in seconds with just your name and email. Your digital discount card is generated instantly — no fees, no insurance required, no catch.',
      tip: 'Your card never expires and works for your whole family',
      color: colors.accent
    },
    {
      number: '4',
      icon: <MapPin size={28} />,
      title: 'Save at the Pharmacy',
      description: 'Show your digital card to the pharmacist when picking up your prescription. They\'ll apply the discount right at the counter — it\'s that simple.',
      tip: 'Save your card to your phone\'s wallet for easy access',
      color: colors.warning
    }
  ];

  const benefits = [
    {
      icon: <DollarSign size={22} />,
      title: '100% Free',
      description: 'No sign-up fees, no monthly charges, no hidden costs. Ever.'
    },
    {
      icon: <Shield size={22} />,
      title: 'No Insurance Needed',
      description: 'Works whether you have insurance or not. Compare to find the best price.'
    },
    {
      icon: <Users size={22} />,
      title: 'Whole Family Coverage',
      description: 'One card works for everyone in your household — even your pets.'
    },
    {
      icon: <MapPin size={22} />,
      title: '67,000+ Pharmacies',
      description: 'Accepted at CVS, Walgreens, Walmart, Rite Aid, Costco, and more.'
    },
    {
      icon: <Clock size={22} />,
      title: 'Instant Access',
      description: 'Get your digital card in seconds. No waiting, no approval process.'
    },
    {
      icon: <Smartphone size={22} />,
      title: 'Digital & Printable',
      description: 'Use on your phone or print it out. Also emailed to you for safekeeping.'
    }
  ];

  const comparisons = [
    { item: 'Atorvastatin (Lipitor generic)', retail: '$135', discount: '$12', savings: '91%' },
    { item: 'Lisinopril', retail: '$45', discount: '$4', savings: '91%' },
    { item: 'Metformin', retail: '$60', discount: '$6', savings: '90%' },
    { item: 'Sertraline (Zoloft generic)', retail: '$90', discount: '$11', savings: '88%' },
    { item: 'Omeprazole (Prilosec generic)', retail: '$75', discount: '$10', savings: '87%' }
  ];

  return (
    <div style={{
      fontFamily: "'Inter', -apple-system, sans-serif",
      background: colors.bg,
      minHeight: '100vh'
    }}>
      {/* Hero Section */}
      <section style={{
        background: colors.primary,
        padding: isMobile ? '48px 16px 56px' : '80px 24px 100px',
        textAlign: 'center',
        marginTop: 0
      }}>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <h1 style={{
            fontSize: isMobile ? 28 : 48,
            fontWeight: 800,
            color: 'white',
            marginBottom: 14,
            lineHeight: 1.2
          }}>
            How It Works
          </h1>
          <p style={{
            fontSize: isMobile ? 15 : 20,
            color: 'rgba(255,255,255,0.85)',
            lineHeight: 1.5,
            maxWidth: 550,
            margin: '0 auto'
          }}>
            Save up to 80% on your prescriptions in 4 simple steps. No insurance required.
          </p>
        </div>
      </section>

      {/* Steps Section */}
      <section style={{ padding: isMobile ? '40px 16px' : '80px 24px' }}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          {steps.map((step, index) => (
            <React.Fragment key={index}>
              <div style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? 16 : 28,
                alignItems: isMobile ? 'center' : 'flex-start',
                textAlign: isMobile ? 'center' : 'left',
                marginBottom: 20
              }}>
                {/* Step Number + Icon */}
                <div style={{
                  width: 72,
                  height: 72,
                  borderRadius: 20,
                  background: `${step.color}10`,
                  border: `2px solid ${step.color}25`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: step.color,
                  flexShrink: 0,
                  position: 'relative'
                }}>
                  {step.icon}
                  <div style={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    background: step.color,
                    color: 'white',
                    fontSize: 13,
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {step.number}
                  </div>
                </div>

                {/* Step Content */}
                <div style={{ flex: 1 }}>
                  <h3 style={{
                    fontSize: isMobile ? 20 : 22,
                    fontWeight: 700,
                    color: colors.gray900,
                    marginBottom: 8
                  }}>
                    {step.title}
                  </h3>
                  <p style={{
                    fontSize: isMobile ? 15 : 16,
                    color: colors.gray600,
                    lineHeight: 1.7,
                    marginBottom: 12
                  }}>
                    {step.description}
                  </p>
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '8px 14px',
                    background: `${step.color}08`,
                    borderRadius: 8,
                    fontSize: 13,
                    fontWeight: 600,
                    color: step.color
                  }}>
                    <CheckCircle size={14} />
                    Pro tip: {step.tip}
                  </div>
                </div>
              </div>

              {/* Arrow between steps */}
              {index < steps.length - 1 && (
                <div style={{
                  display: 'flex',
                  justifyContent: isMobile ? 'center' : 'flex-start',
                  padding: isMobile ? '8px 0' : '8px 0 8px 32px',
                  marginBottom: 20
                }}>
                  <ArrowDown size={24} style={{ color: colors.gray300 || '#cbd5e1' }} />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* Benefits Grid */}
      <section style={{
        background: 'white',
        padding: isMobile ? '40px 16px' : '60px 24px'
      }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <h2 style={{
            fontSize: isMobile ? 22 : 32,
            fontWeight: 800,
            color: colors.gray900,
            textAlign: 'center',
            marginBottom: 8
          }}>
            Why Choose Rx.ChatWithDr?
          </h2>
          <p style={{
            fontSize: isMobile ? 15 : 17,
            color: colors.gray500,
            textAlign: 'center',
            marginBottom: isMobile ? 32 : 48,
            maxWidth: 500,
            margin: '0 auto',
            marginBottom: isMobile ? 32 : 48
          }}>
            More than just a discount card — it's your prescription savings partner
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: isMobile ? 14 : 20
          }}>
            {benefits.map((benefit, i) => (
              <div key={i} style={{
                padding: isMobile ? 20 : 28,
                borderRadius: 14,
                border: `1px solid ${colors.gray200}`,
                background: colors.bg,
                transition: 'transform 0.2s, box-shadow 0.2s'
              }}
                onMouseOver={(e) => {
                  if (!isMobile) {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.08)';
                  }
                }}
                onMouseOut={(e) => {
                  if (!isMobile) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }
                }}
              >
                <div style={{
                  width: 44,
                  height: 44,
                  borderRadius: 10,
                  background: `${colors.primary}10`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: colors.primary,
                  marginBottom: 14
                }}>
                  {benefit.icon}
                </div>
                <h3 style={{
                  fontSize: isMobile ? 16 : 17,
                  fontWeight: 700,
                  color: colors.gray900,
                  marginBottom: 6
                }}>
                  {benefit.title}
                </h3>
                <p style={{
                  fontSize: 14,
                  color: colors.gray600,
                  lineHeight: 1.6,
                  margin: 0
                }}>
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Savings Example Table */}
      <section style={{ padding: isMobile ? '40px 16px' : '60px 24px' }}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <h2 style={{
            fontSize: isMobile ? 22 : 32,
            fontWeight: 800,
            color: colors.gray900,
            textAlign: 'center',
            marginBottom: 8
          }}>
            Real Savings Examples
          </h2>
          <p style={{
            fontSize: isMobile ? 15 : 17,
            color: colors.gray500,
            textAlign: 'center',
            marginBottom: isMobile ? 24 : 40,
            maxWidth: 500,
            margin: '0 auto',
            paddingBottom: isMobile ? 24 : 40
          }}>
            See how much you could save on popular medications
          </p>

          <div style={{
            background: 'white',
            borderRadius: 16,
            overflow: 'hidden',
            border: `1px solid ${colors.gray200}`,
            boxShadow: '0 2px 12px rgba(0,0,0,0.06)'
          }}>
            {/* Table Header */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr auto auto' : '2fr 1fr 1fr 1fr',
              padding: isMobile ? '14px 16px' : '16px 24px',
              background: colors.gray900,
              color: 'white',
              fontSize: isMobile ? 12 : 14,
              fontWeight: 600,
              gap: isMobile ? 8 : 0
            }}>
              <div>Medication</div>
              <div style={{ textAlign: 'center' }}>Retail</div>
              <div style={{ textAlign: 'center' }}>Our Price</div>
              {!isMobile && <div style={{ textAlign: 'center' }}>Savings</div>}
            </div>

            {/* Table Rows */}
            {comparisons.map((row, i) => (
              <div key={i} style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr auto auto' : '2fr 1fr 1fr 1fr',
                padding: isMobile ? '14px 16px' : '16px 24px',
                borderTop: `1px solid ${colors.gray100}`,
                alignItems: 'center',
                gap: isMobile ? 8 : 0,
                background: i % 2 === 0 ? 'transparent' : colors.gray100 + '60'
              }}>
                <div>
                  <span style={{
                    fontSize: isMobile ? 14 : 15,
                    fontWeight: 600,
                    color: colors.gray800
                  }}>
                    {row.item}
                  </span>
                </div>
                <div style={{
                  textAlign: 'center',
                  fontSize: isMobile ? 14 : 15,
                  color: colors.gray500,
                  textDecoration: 'line-through'
                }}>
                  {row.retail}
                </div>
                <div style={{
                  textAlign: 'center',
                  fontSize: isMobile ? 16 : 18,
                  fontWeight: 700,
                  color: colors.success
                }}>
                  {row.discount}
                </div>
                {!isMobile && (
                  <div style={{
                    textAlign: 'center'
                  }}>
                    <span style={{
                      display: 'inline-block',
                      padding: '4px 12px',
                      borderRadius: 20,
                      background: `${colors.success}15`,
                      color: colors.success,
                      fontSize: 13,
                      fontWeight: 700
                    }}>
                      {row.savings}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>

          <p style={{
            fontSize: 12,
            color: colors.gray400,
            textAlign: 'center',
            marginTop: 12,
            fontStyle: 'italic'
          }}>
            * Prices are estimates and may vary by pharmacy and location. Based on 30-day supply at common dosages.
          </p>
        </div>
      </section>

      {/* Need a Prescription CTA */}
      <section style={{
        background: `linear-gradient(135deg, ${colors.gray900} 0%, ${colors.secondary} 100%)`,
        padding: isMobile ? '40px 16px' : '60px 24px',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <div style={{
            width: 56,
            height: 56,
            borderRadius: 16,
            background: 'rgba(255,255,255,0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
            color: colors.accent
          }}>
            <Heart size={28} />
          </div>
          <h2 style={{
            fontSize: isMobile ? 22 : 28,
            fontWeight: 700,
            color: 'white',
            marginBottom: 12
          }}>
            Need a Prescription?
          </h2>
          <p style={{
            fontSize: isMobile ? 15 : 17,
            color: 'rgba(255,255,255,0.7)',
            marginBottom: 28,
            lineHeight: 1.6
          }}>
            Chat with a licensed doctor online through ChatWithDr.com. Get diagnosed, treated, and prescribed — all from your phone for just $39.99.
          </p>
          <a
            href="https://chatwithdr.com"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: isMobile ? '14px 28px' : '16px 32px',
              background: 'white',
              color: colors.primary,
              border: 'none',
              borderRadius: 10,
              fontSize: isMobile ? 15 : 16,
              fontWeight: 700,
              textDecoration: 'none',
              cursor: 'pointer'
            }}
          >
            Visit ChatWithDr.com <ArrowRight size={18} />
          </a>
        </div>
      </section>

      {/* Final CTA */}
      <section style={{
        background: 'white',
        padding: isMobile ? '40px 16px' : '60px 24px',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <h2 style={{
            fontSize: isMobile ? 22 : 28,
            fontWeight: 700,
            color: colors.gray900,
            marginBottom: 12
          }}>
            Ready to Start Saving?
          </h2>
          <p style={{
            fontSize: isMobile ? 15 : 16,
            color: colors.gray600,
            marginBottom: 28,
            lineHeight: 1.6
          }}>
            Get your free discount card now and start saving on your next prescription.
          </p>
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: 12,
            justifyContent: 'center'
          }}>
            <button
              onClick={() => navigate('/card')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                padding: isMobile ? '14px 24px' : '14px 28px',
                background: colors.primary,
                color: 'white',
                border: 'none',
                borderRadius: 10,
                fontSize: isMobile ? 15 : 16,
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              <CreditCard size={18} /> Get Free Card
            </button>
            <button
              onClick={() => navigate('/search')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                padding: isMobile ? '14px 24px' : '14px 28px',
                background: 'transparent',
                color: colors.primary,
                border: `2px solid ${colors.primary}`,
                borderRadius: 10,
                fontSize: isMobile ? 15 : 16,
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              <Search size={18} /> Search Medications
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HowItWorksPage;